import { Subject } from 'rxjs';
import { IDynamicConflictBroadcast, IDynamicConflictState } from '../models';

/**
 * Conflict resolution (see IDynamicConflictResolution) for DynamicRenderService (WI 31925: split out of it,
 * unchanged). Only a source-mapping registry plus fan-out onto the service's conflictUpdate$; no conflict state
 * is persisted here.
 */
export class DynamicConflictRegistry {
  /**
   * target formControlName -> (source formControlName -> source label) whose updateOnValueChange
   * targets it. Built by per-control self-registration (NOT from activeModelGroup, which is empty in
   * Mode A), so we can light up every contributing source's label when a conflict is raised AND name
   * the whole source combination (Company + Customer + Facility) in the conflict popover.
   */
  private readonly sourceMap: Map<string, Map<string, string>> = new Map();

  /** The bus is read on each send, so a reassigned conflictUpdate$ is the one that emits. */
  constructor(private readonly bus: () => Subject<IDynamicConflictBroadcast>) {}

  /** A source control declares it pushes into `targetName` (from its updateOnValueChange). */
  register(sourceName: string, targetName: string, sourceLabel?: string): void {
    if (!sourceName || !targetName) {
      return;
    }
    let sources = this.sourceMap.get(targetName);
    if (!sources) {
      sources = new Map<string, string>();
      this.sourceMap.set(targetName, sources);
    }
    // keep an existing label if re-registered without one (don't blank it out)
    sources.set(sourceName, sourceLabel ?? sources.get(sourceName) ?? '');
  }

  /** Remove a source mapping (called on the source control's destroy so closed forms leave nothing stale). */
  deregister(sourceName: string, targetName: string): void {
    const sources = this.sourceMap.get(targetName);
    if (sources) {
      sources.delete(sourceName);
      if (sources.size === 0) {
        this.sourceMap.delete(targetName);
      }
    }
  }

  /**
   * Registered sources for a target with their labels — lets the target assemble the full source
   * combination (Company + Customer + Facility) and their current values for the conflict popover.
   */
  sourceMetaFor(
    targetName: string
  ): Array<{ formControlName: string; label?: string }> {
    return Array.from(this.sourceMap.get(targetName)?.entries() ?? []).map(
      ([formControlName, label]) => ({
        formControlName,
        label: label || undefined,
      })
    );
  }

  /** Raise a conflict: notify the target label and every contributing source label. */
  raise(state: IDynamicConflictState): void {
    if (!state?.targetFormControlName) {
      return;
    }
    this.bus().next({
      formControlName: state.targetFormControlName,
      role: 'target',
      type: 'raise',
      state,
    });
    for (const sourceName of this.sourcesFor(state.targetFormControlName)) {
      this.bus().next({
        formControlName: sourceName,
        role: 'source',
        type: 'raise',
        state,
      });
    }
  }

  /** User chose how to resolve a conflict on `targetName`. Fans out to target + sources. */
  resolve(targetName: string, choice: 'default' | 'keep'): void {
    if (!targetName) {
      return;
    }
    this.bus().next({
      formControlName: targetName,
      role: 'target',
      type: 'resolve',
      targetFormControlName: targetName,
      choice,
    });
    for (const sourceName of this.sourcesFor(targetName)) {
      this.bus().next({
        formControlName: sourceName,
        role: 'source',
        type: 'resolve',
        targetFormControlName: targetName,
        choice,
      });
    }
  }

  /** Clear any conflict state for `targetName` (target + sources). */
  clear(targetName: string): void {
    if (!targetName) {
      return;
    }
    this.bus().next({
      formControlName: targetName,
      role: 'target',
      type: 'clear',
      targetFormControlName: targetName,
    });
    for (const sourceName of this.sourcesFor(targetName)) {
      this.bus().next({
        formControlName: sourceName,
        role: 'source',
        type: 'clear',
        targetFormControlName: targetName,
      });
    }
  }

  /** Source control names registered for a target (the labels that should light up with the icon). */
  private sourcesFor(targetName: string): Array<string> {
    return Array.from(this.sourceMap.get(targetName)?.keys() ?? []);
  }
}
